import React, { useCallback } from 'react';
import { useRealTimeStore } from '../../store/realTimeStore';

import type { SectionInfo } from '../../types';
import { Section } from './Section';

// Pixel positioning is inacurate on browsers.
// To minimize the risk of unwanted gap between inactive and active sections,
// we add an extra pixel to the width of inactive section
const INACTIVE_EXTRA_PIXEL_WIDTH = 1;

export const round = (num: number): number => {
  const m = Number((Math.abs(num) * 100).toPrecision(8));

  return (Math.ceil(m) / 100) * Math.sign(num);
};

interface Props {
  width: number;
  duration: number;
  isHoveringTimeline: boolean;
  isHoveringSurrounding: boolean;
  onMouseDown: (clientX: number, uiPosition: string) => void;
}

export const SectionListUnmemo = ({
  width,
  duration,
  isHoveringTimeline,
  isHoveringSurrounding,
  onMouseDown,
}: Props): JSX.Element => {
  const sectionInfoList = useRealTimeStore((state) => state.sectionInfoList);
  const isDragging = useRealTimeStore((state) => state.isDragging);
  const activeSectionIndex = useRealTimeStore(
    useCallback(
      (state) => (state.isDragging ? state.draggedSectionIndex : state.currentSectionIndex),
      [],
    ),
  );
  const activePageIndex = useRealTimeStore(
    useCallback(
      (state) => (state.isDragging ? state.draggedPageIndex : state.currentPageIndex),
      [],
    ),
  );
  const xActiveProgressInPx = useRealTimeStore(
    useCallback(
      (state) =>
        state.isDragging
          ? (state.draggedTime / duration) * width
          : (state.currentTimeWithLessPrecision / duration) * width,
      [duration, width],
    ),
  );
  const hoveredPageIndex = useRealTimeStore((state) => state.hoveredPageIndex);

  const computeRelativeProgress = useCallback(
    // eslint-disable-next-line func-params-args/func-params
    (
      sectionInfo: SectionInfo,
      sectionInfoIndex: number,
      newActiveSectionIndex: number,
      xPositionInPx: number,
    ) => {
      if (sectionInfoIndex < newActiveSectionIndex) {
        return 100;
      }
      if (sectionInfoIndex > newActiveSectionIndex) {
        return 0;
      }
      const xStartPosition = (sectionInfo.startTimeInPercent / 100) * width;
      const xEndPosition =
        ((sectionInfo.startTimeInPercent + sectionInfo.durationInPercent) / 100) * width;
      return ((xPositionInPx - xStartPosition) / (xEndPosition - xStartPosition)) * 100;
    },
    [width],
  );

  return (
    <>
      {sectionInfoList.map((sectionInfo, index) => {
        const isHoveringAroundSection =
          (isDragging && activePageIndex === sectionInfo.pageIndex) ||
          (!isDragging && hoveredPageIndex === sectionInfo.pageIndex);

        return (
          <Section
            isFirst={index === 0}
            isLast={index === sectionInfoList.length - 1}
            isBeforeInactiveSection={sectionInfoList[index + 1]?.isActive === false}
            isAfterInactiveSection={sectionInfoList[index - 1]?.isActive === false}
            key={sectionInfo.index}
            xPositionInPx={round(
              (sectionInfo.startTimeInMs / useRealTimeStore.getState().duration) * width +
                (sectionInfo.isActive ? 0 : -INACTIVE_EXTRA_PIXEL_WIDTH / 2),
            )}
            xWidthInPx={round(
              (sectionInfo.durationInMs / useRealTimeStore.getState().duration) * width +
                (sectionInfo.isActive ? 0 : INACTIVE_EXTRA_PIXEL_WIDTH),
            )}
            xProgressInPercent={computeRelativeProgress(
              sectionInfo,
              index,
              activeSectionIndex,
              xActiveProgressInPx,
            )}
            onMouseDown={onMouseDown}
            isAtLeastHalfExpanded={isHoveringAroundSection && isHoveringSurrounding}
            isHoveringOnSection={(isDragging || isHoveringTimeline) && isHoveringAroundSection}
            isSectionWithActivity={sectionInfo.isActive}
            isLastSectionOfPage={sectionInfo.pageIndex !== sectionInfoList[index + 1]?.pageIndex}
          />
        );
      })}
    </>
  );
};

export const SectionList = React.memo(SectionListUnmemo);
